// The lenses (roadmap 3.5): money, time, people, university — across every thread.
import { useEffect, useState } from 'react';
import { api, onChange } from '../api';
import { day, ft, Label, Panel, tc } from '../ui';
import { MoneyTools, QuickEntry, TxLists, useMoneyList } from './MoneyEntry';
import { MoneySettings } from './MoneySettings';

const TABS: Array<[string, string, string]> = [['penz', 'Pénz', 'mint'], ['ido', 'Idő', 'violet'], ['emberek', 'Emberek', 'coral'], ['egyetem', 'Egyetem', 'cyan']];

function useLens<T>(path: string) {
  const [d, setD] = useState<T | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { setD(null); const l = () => api<T>(path).then(setD).catch((e) => setErr(e.message)); l(); return onChange(l); }, [path]);
  return { d, err };
}
const Loading = ({ err }: { err: string }) => (err ? <p className="err">{err}</p> : <p className="muted">Betöltés…</p>);
const STATE: Record<string, [string, string]> = { ok: ['mint', 'rendben'], tight: ['amber', 'szűkös'], over: ['coral', 'mínuszban'], unknown: ['cyan', 'nincs egyenleg'] };

function Money() {
  const { d, err } = useLens<any>('/api/lens/money');
  const list = useMoneyList();
  const [openId, setOpen] = useState<string | null>(null);
  const [cfg, setCfg] = useState(false);
  // the "+" on the Most screen opens this page with the quick line focused
  const focus = /[?&]uj=1/.test(location.hash);
  const entry = (
    <>
      <QuickEntry focus={focus} onSaved={(t) => { list.load(); if (t.needs_review) setOpen(String(t.id)); }} />
      <div className="j-row">
        {list.d && <MoneyTools d={list.d} reload={list.load} />}
        <button type="button" className={`j-chip cfg-toggle ${cfg ? 'on' : ''}`} style={{ '--c': 'var(--cyan)' } as any} onClick={() => setCfg(!cfg)}>{cfg ? 'Vissza az áttekintéshez' : 'Beállítás'}</button>
      </div>
    </>
  );
  if (cfg) return <div className="j-stack">{entry}<MoneySettings /></div>;
  if (!d) return <div className="j-stack">{entry}<Loading err={err} /></div>;
  const [c, label] = STATE[d.state] ?? STATE.unknown!;
  const maxCat = Math.max(1, ...d.spentByCategory.map((r: any) => r.spent));
  return (
    <div className="j-stack">
      {entry}
      <Panel c={c} className="j-next">
        <Label c={c}>Fizetésig szabad · {label}</Label>
        <h2 className="j-num big-num">{d.free != null ? ft(d.free) : '–'}</h2>
        <p>{d.perDay != null ? `napi ${ft(d.perDay)} · ` : ''}{d.cycle.daysLeft} nap ({day(d.cycle.next)}-ig) · egyenleg {ft(d.balance)} ({d.balanceAt})</p>
        <p className="muted small">A szokásos változó költésed a hátralévő napokra kb. {ft(d.needVariable)}; félretehető most: {ft(d.safeToSave)}.</p>
      </Panel>
      <Panel c="coral"><Label c="coral">Még jön ebben a ciklusban · −{ft(d.fixedOut)} / +{ft(d.fixedIn)}</Label>
        {d.fixedUpcoming.map((f: any, i: number) => (
          <div className="j-slot" key={i}><div className={`t ${f.late ? 'late' : ''}`}>{day(f.due)}</div>
            <div className="row-split"><span>{f.name}{f.loan ? ' · hitel' : f.planned ? ' · tervezett' : f.auto ? ' · havi' : ''}</span><span className="j-num">{f.kind === 'in' ? '+' : '−'}{ft(f.amount)}</span></div></div>
        ))}
      </Panel>
      {d.budgets.length > 0 && (
        <Panel c="amber"><Label c="amber">Keretek</Label>
          {d.budgets.map((b: any) => (
            <div key={b.category} className="bar-row"><div className="row-split"><span>{b.category}</span><span className="j-num">{ft(b.spent)} / {ft(b.limit)}</span></div>
              <div className="bar"><i style={{ width: `${Math.min(100, (100 * b.spent) / Math.max(1, b.limit))}%`, background: b.spent > b.limit ? 'var(--coral)' : 'var(--amber)' }} /></div></div>
          ))}
        </Panel>
      )}
      <Panel c="mint"><Label c="mint">Ebben a ciklusban elköltve · {ft(d.spentTotal)}</Label>
        {d.spentByCategory.map((r: any) => (
          <div key={r.category} className="bar-row"><div className="row-split"><span>{r.category} <em className="muted">· {r.group}</em></span><span className="j-num">{ft(r.spent)}</span></div>
            <div className="bar"><i style={{ width: `${(100 * r.spent) / maxCat}%` }} /></div></div>
        ))}
      </Panel>
      <Panel c="violet"><Label c="violet">Szálanként</Label>
        {d.threads.map((t: any) => (
          <a key={t.id} href={`#/szal/${t.id}`} className="row-split thread-money" style={tc(t.id)}><span><span className="dot" /> {t.name}</span><span className="j-num">{ft(t.net)}</span></a>
        ))}
      </Panel>
      <Panel c="cyan"><Label c="cyan">Átlagos hónap ({d.baseline.count} hónap)</Label>
        <div className="kv"><span>bevétel</span><b className="j-num">{ft(d.baseline.income)}</b><span>fix költség</span><b className="j-num">{ft(d.baseline.fixed)}</b>
          <span>változó költés</span><b className="j-num">{ft(d.baseline.variable)}</b><span>marad</span><b className="j-num">{ft(d.baseline.net)}</b></div>
      </Panel>
      {list.d && <TxLists d={list.d} more={list.more} openId={openId} setOpen={setOpen} />}
    </div>
  );
}

function Time() {
  const { d, err } = useLens<any>('/api/lens/time');
  if (!d) return <Loading err={err} />;
  const max = Math.max(1, ...d.threads.map((t: any) => t.events));
  const focus = new Set(d.focus.map((f: any) => f.id));
  return (
    <div className="j-stack">
      <p className="muted small">Az utolsó 4 hét: melyik szálban mennyi minden történt (a sötétebb rész a saját lépéseid). A pontos időt a naptár és a commitok hozzák majd.</p>
      <Panel c="violet">
        {d.threads.map((t: any) => (
          <a key={t.id} href={`#/szal/${t.id}`} className="bar-row link" style={tc(t.id)}>
            <div className="row-split"><span>{t.name}{focus.has(t.id) ? ' · fókusz' : ''}</span><span className="j-num">{t.own} saját · {t.events} összes</span></div>
            <div className="bar"><i style={{ width: `${(100 * t.events) / max}%`, background: 'var(--tc)' }} /><b style={{ width: `${(100 * t.own) / max}%` }} /></div>
          </a>
        ))}
        {d.focus.filter((f: any) => !d.threads.some((t: any) => t.id === f.id)).map((f: any) => <p key={f.id} className="warn">{f.name} fókuszban van, de 4 hete nem történt benne semmi.</p>)}
      </Panel>
    </div>
  );
}

function People() {
  const { d, err } = useLens<any>('/api/lens/people');
  if (!d) return <Loading err={err} />;
  const list = (rows: any[], extra: (r: any) => string) => rows.map((r) => (
    <div key={r.id} className="j-slot"><div className="t">{day(r.last_at)}</div><div><div>{r.name}</div><div className="w">{extra(r)}</div></div></div>
  ));
  return (
    <div className="j-stack">
      <Panel c="mint"><Label c="mint">Most velük vagy kapcsolatban</Label>{list(d.active, (r) => `${r.events_90d} esemény 90 nap alatt${r.threads ? ' · ' + r.threads : ''}`)}</Panel>
      <Panel c="coral"><Label c="coral">Régen jelentkeztél</Label>
        {d.fading.length ? list(d.fading, (r) => `${r.events_all} közös esemény${r.threads ? ' · ' + r.threads : ''}`) : <p className="muted">Nincs ilyen.</p>}</Panel>
      <Panel c="violet"><Label c="violet">Több szálban is fontosak</Label>{list(d.bridges, (r) => r.threads)}</Panel>
    </div>
  );
}

function Uni() {
  const { d, err } = useLens<any>('/api/lens/uni');
  if (!d) return <Loading err={err} />;
  const now = d.subjects.filter((s: any) => s.semester === d.current || s.next_exam || s.next_class);
  const rest = d.subjects.filter((s: any) => !now.includes(s) && s.grade == null && s.status !== 'kész');
  const row = (s: any) => (
    <div key={s.id} className="j-slot"><div className="t">{s.next_exam ? day(s.next_exam) : s.next_class ? day(s.next_class) : s.grade != null ? s.grade : '–'}</div>
      <div><div>{s.name} {s.credits ? <em className="muted">· {s.credits} kr</em> : null}</div>
        <div className="w">{[s.next_exam && 'vizsga', !s.next_exam && s.next_class && 'óra', s.signature && `aláírás: ${s.signature}`, s.exam && `vizsga: ${s.exam}`].filter(Boolean).join(' · ')}</div></div></div>
  );
  return (
    <div className="j-stack">
      <Panel c="cyan"><Label c="cyan">Ebben a félévben · {now.length} tárgy</Label>{now.map(row)}</Panel>
      <Panel c="violet"><Label c="violet">Eddig · {d.doneCount} tárgy, {d.credits} kredit</Label></Panel>
      {rest.length > 0 && <Panel c="coral"><Label c="coral">Még hátravan · {rest.length}</Label>{rest.map(row)}</Panel>}
    </div>
  );
}

export function Lenses({ tab }: { tab: string }) {
  const t = TABS.find((x) => x[0] === tab) ?? TABS[0]!;
  return (
    <div className="j-stack">
      <div className="tabs">{TABS.map(([k, l, c]) => <a key={k} href={`#/lencse/${k}`} className={`j-chip j-c-${c} ${k === t[0] ? 'on' : ''}`}>{l}</a>)}</div>
      {t[0] === 'penz' ? <Money /> : t[0] === 'ido' ? <Time /> : t[0] === 'emberek' ? <People /> : <Uni />}
    </div>
  );
}
